import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "./ui/resizable";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  ShoppingBasket,
  StickyNote,
  Trash2,
  X as CloseIcon,
  ChevronLeft,
  ChevronRight,
  Highlighter,
  Plus,
  Check,
  ArrowUp,
  ArrowDown,
  GraduationCap,
  NotebookPen,
} from "lucide-react";
import { api } from "../lib/api";
import { formatHebrewRef, numberToHebrew, talmudDafLabel } from "../lib/hebrewRef";
import { numberToHebrewLetter } from "../lib/hebrewNumerals";
import { useBasket } from "../context/BasketContext";
import { useLearning } from "../context/LearningContext";
import { toast } from "sonner";
import CommentaryAccordion from "./CommentaryAccordion";
import useRequireAuth from "../hooks/useRequireAuth";
import LessonBuilder from "./LessonBuilder";
import SaveToNotebookModal from "./SaveToNotebookModal";
import { useLanguage } from "../context/LanguageContext";

/**
 * Mikraot Gedolot Shell — desktop Study Mode 3-pane workspace.
 *
 *   ┌──────────────┬──────────────────────┬──────────────┐
 *   │  RIGHT PANE  │      MIDDLE PANE     │  LEFT PANE   │
 *   │   Tanakh     │   Commentaries (e.g. │   Basket +   │
 *   │   text       │   Rashi  ‖  Ramban)  │   Notes      │
 *   └──────────────┴──────────────────────┴──────────────┘
 *
 * The user clicks a verse on the right; the middle pane immediately fetches
 * its commentaries and renders them side-by-side. The left pane shows the
 * lesson basket and any personal notes belonging to the active verse.
 */
export default function MikraotShell({
  book,
  bookHe,
  slug,
  chapter,
  total,
  verses,            // chapter verses [{verse, he, en}]
  versionEn,         // Sefaria English version title (e.g. "The Koren Jerusalem Bible")
  loading,
  activeVerse,
  setActiveVerse,
  highlights,
  notesByVerse,
  showVerseNums,
  onPrevChapter,
  onNextChapter,
  prevDisabled,
  nextDisabled,
}) {
  const basket = useBasket();
  const learning = useLearning();
  const { isEn } = useLanguage();
  const { t } = useTranslation();
  const [comm, setComm] = useState([]);
  const [commLoading, setCommLoading] = useState(false);
  const [activeCommentator, setActiveCommentator] = useState(null); // Track which commentator is expanded

  // Talmud books index amudim sequentially (chapter 1 = "2a"). Display
  // them as "דף ב׳ ע״א" in HE / "2a" in EN everywhere instead of the
  // "פרק א׳" wording. Tanakh/Mishnah stay on the integer/gematria path.
  const isTalmud = book?.kind === "talmud";
  const chapterWord = isEn ? "Chapter" : "פרק";
  const chapterNumeral = (n) => (isEn ? String(n) : numberToHebrewLetter(n));
  const chapterLabel = isTalmud
    ? talmudDafLabel(chapter)
    : `${chapterWord} ${chapterNumeral(chapter)}`;
  const siblingLabel = (n) =>
    isTalmud ? talmudDafLabel(n) : `${chapterWord} ${chapterNumeral(n)}`;
  const progressLabel = isTalmud
    ? `${chapter} / ${total}`
    : `${numberToHebrewLetter(chapter)} / ${numberToHebrewLetter(total)}`;

  // Commentator catalogs — pick the right one for the book kind so we don't
  // ask Sefaria for Rashi-on-Mishnah (empty 404 storm) or Bartenura-on-Bereshit.
  // Tanakh list is the original 20-name lineup; Mishnah list is the verified
  // set of commentaries that actually return text from Sefaria on Mishnah.
  const TANAKH_COMMENTATORS = [
    "Rashi", "Ramban", "Ibn Ezra", "Or HaChaim", "Rashbam", "Bekhor Shor",
    "Chizkuni", "Radak", "Sforno", "Malbim", "Metzudat David", "Metzudat Zion",
    "Ralbag", "Abarbanel", "Kli Yakar", "Alshich", "Haamek Davar",
    "Chatam Sofer",
    "Onkelos", "Targum Jonathan",
  ];
  const MISHNAH_COMMENTATORS = [
    "Bartenura", "Tosafot Yom Tov", "Rambam", "Yachin", "Melekhet Shelomoh",
  ];
  // Talmud Bavli commentators. Sefaria treats "Rashi on Berakhot" as a
  // distinct work from "Rashi on Genesis" — different ref shapes, separate
  // catalog entries — so we ALWAYS request the Talmud-scoped list when the
  // book.kind === "talmud" to avoid cross-corpus contamination.
  const TALMUD_COMMENTATORS = [
    "Rashi", "Tosafot", "Meiri", "Tosafot HaRosh", "Penei Yehoshua",
    "Rashash", "Steinsaltz",
  ];
  const allCommentatorNames =
    book?.kind === "mishnah"
      ? MISHNAH_COMMENTATORS
      : book?.kind === "talmud"
      ? TALMUD_COMMENTATORS
      : TANAKH_COMMENTATORS;

  // Fetch commentaries when active verse changes - request ALL commentators
  useEffect(() => {
    if (!activeVerse || !slug || !chapter) {
      setComm([]);
      return;
    }
    let cancel = false;
    setCommLoading(true);
    api
      .commentaries(slug, chapter, activeVerse.verse, allCommentatorNames)
      .then((res) => {
        if (cancel) return;
        // Filter out empty commentaries - only show those with actual text (Hebrew or English)
        const withText = (res?.commentaries || []).filter((c) => (c.he && c.he.trim()) || (c.en && c.en.trim()));
        setComm(withText);
      })
      .catch(() => !cancel && setComm([]))
      .finally(() => !cancel && setCommLoading(false));
    return () => { cancel = true; };
  }, [activeVerse, slug, chapter]);

  // Auto-pick verse 1 when entering the chapter so the panes are never empty.
  useEffect(() => {
    if (!loading && verses?.length > 0 && !activeVerse) {
      setActiveVerse(verses[0]);
    }
  }, [loading, verses, activeVerse, setActiveVerse]);

  return (
    <div
      className="mikraot-shell relative"
      style={{ height: "calc(100vh - 4.5rem)" }}
      data-testid="mikraot-shell"
      dir={isEn ? "ltr" : "rtl"}
    >
      <ResizablePanelGroup
        direction="horizontal"
        autoSaveId="mikraot-study-layout"
        className="h-full w-full"
      >
        {/* RIGHT (DOM-first in RTL) — Tanakh text */}
        <ResizablePanel defaultSize={32} minSize={22} maxSize={50} order={1}>
          <div
            className="h-full overflow-y-auto overscroll-contain px-5 sm:px-7 py-6 bg-card/30 border-s border-gold/20"
            data-testid="mikraot-pane-text"
          >
            <header className="text-center mb-5 pb-4 border-b border-gold/15">
              <p className="font-latin italic text-[10px] tracking-widest uppercase text-gold mb-1">
                {isTalmud ? `Daf ${chapter}` : `Chapter ${chapter}`}
              </p>
              <h2 className="font-heading text-2xl text-foreground">
                {isEn
                  ? `${book?.en || slug || ""} · ${
                      isTalmud ? talmudDafLabel(chapter) : `Chapter ${chapter}`
                    }`
                  : (isTalmud
                      ? `${bookHe || book?.he || ""} · ${chapterLabel}`
                      : formatHebrewRef(book?.en || bookHe, chapter, undefined, "chapter-only"))}
              </h2>
            </header>

            {loading && (
              <div className="space-y-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-7 shimmer-bg rounded" />
                ))}
              </div>
            )}

            {!loading && verses?.length > 0 && (
              <>
                {/* Per-verse action toolbar — appears when a verse is active.
                    Lets the user add the verse to the basket, highlight it,
                    or jump to the note editor on the left pane. */}
                {activeVerse && (
                  <VerseActionToolbar
                    activeVerse={activeVerse}
                    slug={slug}
                    book={book}
                    bookHe={bookHe}
                    chapter={chapter}
                    basket={basket}
                    learning={learning}
                    highlightId={highlights?.[activeVerse.verse]?.id}
                  />
                )}

                <div className="torah-verse hebrew-text leading-loose text-right">
                  {verses.map((v) => {
                    const hl = highlights?.[v.verse];
                    const hasNote = !!notesByVerse?.[v.verse];
                    const hlClass = hl ? `hl-${hl.color}` : "";
                    const isActive = activeVerse?.verse === v.verse;
                    return (
                      <span
                        key={v.verse}
                        data-testid={`mikraot-verse-${v.verse}`}
                        data-active={isActive ? "true" : "false"}
                        onClick={() => setActiveVerse(v)}
                        className={`verse-row cursor-pointer ${isActive ? "active" : ""} ${hlClass}`}
                      >
                        {showVerseNums && (
                          <span className="verse-num">{numberToHebrewLetter(v.verse)}</span>
                        )}
                        <span>{v.he}</span>
                        {hasNote && <span className="text-gold mx-1">◆</span>}
                        {" "}
                        {/* Inline English translation — rendered ONLY when
                            the UI is in English mode, on its own LTR line
                            below the Hebrew. The Hebrew verse stays
                            visually primary (sacred text shape is kept).
                            Gracefully degrades to a respectful inline
                            note when Sefaria has no English for the verse. */}
                        {isEn && (
                          <span
                            className="block font-latin text-sm text-foreground/85 leading-relaxed mt-1 mb-3 text-left"
                            dir="ltr"
                            lang="en"
                            data-testid={`mikraot-verse-en-${v.verse}`}
                          >
                            {v.en
                              ? v.en
                              : <em className="text-muted-foreground text-xs">
                                  English translation not yet available for this verse — showing the original Hebrew.
                                </em>}
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
                {/* English version attribution — only shown in English
                    mode. Tells the rabbi (and curious learners) exactly
                    which Sefaria translation they're reading, with a
                    direct verify-on-Sefaria link so they can audit the
                    text in its native context. */}
                {isEn && versionEn && (
                  <p
                    className="mt-4 text-[11px] text-muted-foreground italic text-left flex items-center gap-2 flex-wrap"
                    dir="ltr"
                    data-testid="mikraot-version-attribution"
                  >
                    <span>English translation: {versionEn} · via Sefaria</span>
                    <a
                      href={`https://www.sefaria.org/${encodeURIComponent((book && (book.en || book.slug)) || slug || "")}.${chapter}?lang=bi`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="mikraot-verify-sefaria"
                      className="underline decoration-gold/50 hover:decoration-gold text-gold/85 hover:text-gold not-italic"
                    >
                      Verify on Sefaria.org →
                    </a>
                  </p>
                )}
              </>
            )}

            <div className="mt-8 flex items-center justify-between">
              {!prevDisabled ? (
                <Button
                  variant="ghost"
                  onClick={onPrevChapter}
                  className="sacred-btn-ghost"
                  data-testid="mikraot-prev-chapter"
                >
                  <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  {siblingLabel(chapter - 1)}
                </Button>
              ) : (
                <div className="w-20"></div>
              )}
              <span className="font-heading text-xs text-muted-foreground">
                {progressLabel}
              </span>
              <Button
                variant="ghost"
                onClick={onNextChapter}
                disabled={nextDisabled}
                className="sacred-btn-ghost disabled:opacity-30"
                data-testid="mikraot-next-chapter"
              >
                {siblingLabel(chapter + 1)}
                <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* MIDDLE — Commentaries Accordion */}
        <ResizablePanel defaultSize={44} minSize={30} order={2}>
          <div
            className="h-full overflow-y-auto overscroll-contain px-5 sm:px-7 py-6"
            data-testid="mikraot-pane-commentaries"
          >
            <CommentaryPaneHeader activeVerse={activeVerse} bookHe={bookHe} book={book?.en || book?.name} />
            {commLoading ? (
              <CommentaryPaneSkeleton />
            ) : (
              <CommentaryAccordion
                commentaries={comm}
                activeCommentator={activeCommentator}
                onToggle={(name) => {
                  // Toggle: if clicking the same one, close it; otherwise open the new one
                  setActiveCommentator(activeCommentator === name ? null : name);
                }}
              />
            )}
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* LEFT — Basket + Notes */}
        <ResizablePanel defaultSize={24} minSize={18} maxSize={36} order={3}>
          <div
            className="h-full overflow-y-auto overscroll-contain px-4 py-6 bg-card/30 border-e border-gold/20"
            data-testid="mikraot-pane-basket"
          >
            <BasketSection basket={basket} isEn={isEn} />
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );

  // ---- helpers below intentionally hoisted: keep file readable ----
}

function CommentaryPaneHeader({ activeVerse, bookHe, book }) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  return (
    <div className="mb-4">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h3 className="font-heading text-lg text-foreground">
          {activeVerse
            ? (isEn
                ? `${book || bookHe} · ${t("mikraot.verse_pin", { n: activeVerse.verse })}`
                : `${formatHebrewRef(bookHe, undefined)} · פסוק ${numberToHebrew(activeVerse.verse)}`)
            : t("mikraot.tab_commentators")}
        </h3>
        <span className="font-latin italic text-[11px] text-muted-foreground tracking-widest uppercase">
          Mikraot Gedolot
        </span>
      </div>
      {activeVerse?.he && (
        <p
          dir="rtl"
          data-testid="mikraot-active-verse-he"
          className="torah-verse hebrew-text text-base leading-loose text-foreground/90 text-right border-r-2 border-gold/30 pr-3 mb-3"
        >
          {activeVerse.he}
        </p>
      )}
    </div>
  );
}

const HIGHLIGHT_COLORS = [
  { color: "gold",   labelKey: "mikraot.color_source",    reason: "source",    swatch: "#F0C26A" },
  { color: "yellow", labelKey: "mikraot.color_important", reason: "important", swatch: "#FFE082" },
  { color: "blue",   labelKey: "mikraot.color_question",  reason: "question",  swatch: "#90CAF9" },
  { color: "green",  labelKey: "mikraot.color_lesson",    reason: "lesson",    swatch: "#A5D6A7" },
  { color: "red",    labelKey: "mikraot.color_study",     reason: "study",     swatch: "#F48FB1" },
  { color: "purple", labelKey: "mikraot.color_inspire",   reason: "inspire",   swatch: "#CE93D8" },
];

function VerseActionToolbar({ activeVerse, slug, book, bookHe, chapter, basket, learning, highlightId }) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const [busy, setBusy] = useState(false);

  const inBasket = basket.has({
    type: "verse",
    book: slug,
    chapter,
    verse: activeVerse.verse,
  });

  const addVerseToBasket = () => {
    if (inBasket) {
      toast.info(t("mikraot.verse_in_basket"));
      return;
    }
    basket.add({
      type: "verse",
      book: slug,
      book_he: bookHe,
      chapter,
      verse: activeVerse.verse,
      he: activeVerse.he || "",
      en: activeVerse.en || "",
    });
    toast.success(t("mikraot.verse_added", { n: isEn ? activeVerse.verse : numberToHebrew(activeVerse.verse) }));
  };

  const toggleHighlight = async (color, reason) => {
    if (busy) return;
    setBusy(true);
    try {
      if (highlightId) {
        await learning.removeHighlight(highlightId);
        toast.success(t("mikraot.highlight_removed"));
        return;
      }
      await learning.upsertHighlight({
        book: slug,
        chapter,
        verse: activeVerse.verse,
        color,
        reason: reason || null,
      });
      toast.success(t("mikraot.highlight_added"));
    } catch {
      toast.error(t("mikraot.highlight_failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="mikraot-verse-toolbar"
      className="sticky top-0 z-10 -mx-5 sm:-mx-7 mb-4 px-5 sm:px-7 py-2.5 bg-background/95 backdrop-blur-sm border-b border-gold/20 flex items-center gap-2 flex-wrap"
    >
      <span className="text-[11px] text-muted-foreground font-heading whitespace-nowrap ms-1">
        {t("mikraot.verse_pin", { n: isEn ? activeVerse.verse : numberToHebrew(activeVerse.verse) })} ·
      </span>

      <Button
        size="sm"
        variant={inBasket ? "secondary" : "default"}
        onClick={addVerseToBasket}
        data-testid="mikraot-verse-add-to-basket"
        className={
          inBasket
            ? "h-8 gap-1.5 text-xs"
            : "h-8 gap-1.5 text-xs bg-gold text-background hover:bg-gold/90"
        }
      >
        {inBasket ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
        {inBasket ? t("mikraot.in_basket") : t("mikraot.add_to_basket")}
      </Button>

      <div className="flex items-center gap-1.5">
        {highlightId ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => toggleHighlight()}
            disabled={busy}
            data-testid="mikraot-verse-unhighlight"
            className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-destructive"
          >
            <Highlighter className="h-3.5 w-3.5" />
            {t("mikraot.remove_highlight")}
          </Button>
        ) : (
          <>
            <Highlighter className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
            {HIGHLIGHT_COLORS.map(({ color, labelKey, reason, swatch }) => {
              const label = t(labelKey);
              return (
                <button
                  key={color}
                  type="button"
                  onClick={() => toggleHighlight(color, reason)}
                  disabled={busy}
                  aria-label={`${label} (${color})`}
                  title={label}
                  data-testid={`mikraot-verse-highlight-${color}`}
                  className={`h-5 w-5 rounded-full border border-foreground/15 hover:scale-110 transition-transform hl-dot-${color}`}
                  style={{ backgroundColor: swatch }}
                />
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

function BasketSection({ basket, isEn }) {
  const { t } = useTranslation();
  const [noteText, setNoteText] = useState("");
  const [showLB, setShowLB] = useState(false);
  const [showSaveNotebook, setShowSaveNotebook] = useState(false);
  const { gate, RequireAuthModal } = useRequireAuth();

  // Render a verse / commentary / chapter reference the way the active
  // UI language expects it. Hebrew users keep the canonical Hebrew
  // formatting (e.g. "בראשית א׳:ז׳"); English users see the standard
  // Sefaria-style "Genesis 1:7" instead, so the basket reads naturally
  // alongside the rest of the EN chrome.
  const refFor = (it, opts = {}) => {
    if (isEn) {
      const book = it.book || it.book_he || "";
      if (opts.chapterOnly) {
        return `${book} ${it.chapter}`;
      }
      return `${book} ${it.chapter}:${it.verse}`;
    }
    return formatHebrewRef(
      it.book_he || it.book,
      it.chapter,
      opts.chapterOnly ? undefined : it.verse,
      opts.chapterOnly ? "chapter-only" : undefined,
    );
  };

  // Build NotebookEntry payloads from basket items (skip personal notes)
  const notebookPayloads = basket.items
    .filter((it) => it.type !== "note")
    .map((it) => {
      if (it.type === "verse") {
        return {
          source_type: "verse",
          source_book: it.book,
          source_chapter: it.chapter,
          source_verse: it.verse,
          source_ref_he: formatHebrewRef(it.book_he || it.book, it.chapter, it.verse),
          content_he: it.he || "",
          content_text: it.en || "",
        };
      }
      if (it.type === "commentary") {
        return {
          source_type: "commentary",
          source_book: it.book,
          source_chapter: it.chapter,
          source_verse: it.verse,
          source_label: it.commentator_he || it.commentator,
          source_ref_he: formatHebrewRef(it.book_he || it.book, it.chapter, it.verse),
          content_he: it.he || "",
          content_text: it.en || "",
        };
      }
      if (it.type === "chapter") {
        const chHe = numberToHebrew(it.chapter);
        return {
          source_type: "verse",
          source_book: it.book,
          source_chapter: it.chapter,
          source_ref_he: `${it.book_he || it.book} פרק ${chHe}`,
          title: `פרק ${chHe}`,
        };
      }
      return null;
    })
    .filter(Boolean);

  const handleAddNote = () => {
    const text = noteText.trim();
    if (!text) return;
    basket.add({ type: "note", text });
    setNoteText("");
    toast.success(t("mikraot.note_added"));
  };

  const handleClear = () => {
    if (basket.count === 0) return;
    basket.clear();
    toast.success(t("mikraot.basket_cleared"));
  };

  const handleBuild = gate(
    () => {
      if (basket.count === 0) {
        toast.error(t("mikraot.basket_empty_toast"));
        return;
      }
      setShowLB(true);
    },
    { feature: "lesson" }
  );

  return (
    <div data-testid="mikraot-basket">
      <h3 className="font-heading text-sm tracking-[0.25em] uppercase text-gold flex items-center gap-2 mb-2">
        <ShoppingBasket className="h-3.5 w-3.5" strokeWidth={1.5} />
        {t("mikraot.lesson_basket")} {basket.count > 0 && <span className="text-muted-foreground">({basket.count})</span>}
      </h3>

      {basket.count === 0 ? (
        <p
          data-testid="mikraot-basket-empty"
          className="text-xs text-muted-foreground border border-dashed border-border/60 rounded-sm p-3"
        >
          {t("mikraot.basket_empty_hint")}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="mikraot-basket-list">
          {basket.items.map((it, i) => (
            <li
              key={it.id}
              data-testid={`mikraot-basket-item-${i}`}
              data-type={it.type}
              className="rounded-sm border border-border/60 bg-card/60 p-2.5 text-xs"
            >
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-foreground truncate">
                    {it.type === "verse"
                      ? refFor(it)
                      : it.type === "commentary"
                      ? `${isEn ? (it.commentator || it.commentator_he) : (it.commentator_he || it.commentator)} · ${refFor(it)}`
                      : it.type === "chapter"
                      ? refFor(it, { chapterOnly: true })
                      : t("mikraot.personal_note")}
                  </p>
                  {(it.he || it.text) && (
                    <p
                      dir={isEn && it.text ? "ltr" : "rtl"}
                      className={`text-muted-foreground line-clamp-2 mt-1 ${isEn && it.text ? "text-left" : "text-right"}`}
                    >
                      {/* For verse / commentary items we always show the
                          Hebrew source text (it.he) since that is the
                          sacred original. Personal notes (it.text) follow
                          the active UI direction. */}
                      {it.he || it.text}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => basket.move(it.id, -1)}
                    disabled={i === 0}
                    aria-label={t("mikraot.move_up")}
                    data-testid={`mikraot-basket-up-${i}`}
                    className="h-5 w-5 grid place-items-center rounded-sm text-muted-foreground hover:text-gold disabled:opacity-30 transition-colors"
                  >
                    <ArrowUp className="h-3 w-3" strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => basket.move(it.id, +1)}
                    disabled={i === basket.items.length - 1}
                    aria-label={t("mikraot.move_down")}
                    data-testid={`mikraot-basket-down-${i}`}
                    className="h-5 w-5 grid place-items-center rounded-sm text-muted-foreground hover:text-gold disabled:opacity-30 transition-colors"
                  >
                    <ArrowDown className="h-3 w-3" strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => basket.remove(it.id)}
                    aria-label={t("mikraot.remove_from_basket")}
                    data-testid={`mikraot-basket-remove-${i}`}
                    className="h-5 w-5 grid place-items-center rounded-sm text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <CloseIcon className="h-3 w-3" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Quick-add personal note to basket */}
      <div className="mt-4 pt-3 border-t border-border/40">
        <div className="flex items-center gap-1.5 mb-1.5">
          <StickyNote className="h-3 w-3 text-gold" strokeWidth={1.5} />
          <h4 className="font-heading text-[11px] tracking-widest uppercase text-muted-foreground">
            {t("mikraot.add_note_to_basket")}
          </h4>
        </div>
        <Textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder={t("mikraot.note_placeholder")}
          dir={isEn ? "ltr" : "rtl"}
          data-testid="mikraot-basket-note-input"
          className={`min-h-[56px] bg-background/40 border-border/60 focus-visible:ring-gold font-body text-xs ${isEn ? "text-left" : "text-right"}`}
        />
        <div className="mt-1.5 flex justify-end">
          <Button
            onClick={handleAddNote}
            disabled={!noteText.trim()}
            data-testid="mikraot-basket-add-note-btn"
            size="sm"
            className="h-7 gap-1 text-[11px]"
          >
            <Plus className="h-3 w-3" strokeWidth={1.5} />
            {t("mikraot.add")}
          </Button>
        </div>
      </div>

      {/* Action row */}
      <div className="mt-3 pt-3 border-t border-border/40 flex items-center justify-between gap-2 flex-wrap">
        <Button
          variant="ghost"
          onClick={handleClear}
          disabled={basket.count === 0}
          data-testid="mikraot-basket-clear-btn"
          size="sm"
          className="h-7 gap-1 text-[11px] text-muted-foreground"
        >
          <Trash2 className="h-3 w-3" strokeWidth={1.5} />
          {t("mikraot.clear_basket")}
        </Button>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowSaveNotebook(true)}
            disabled={notebookPayloads.length === 0}
            data-testid="mikraot-basket-save-notebook-btn"
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-[11px] border-gold/40 hover:bg-gold/10"
          >
            <NotebookPen className="h-3 w-3" strokeWidth={1.5} />
            {t("mikraot.save_to_notebook")}
          </Button>
          <Button
            onClick={handleBuild}
            disabled={basket.count === 0}
            data-testid="mikraot-basket-build-lesson-btn"
            size="sm"
            className="h-7 gap-1 text-[11px] bg-gold text-background hover:bg-gold/90"
          >
            <GraduationCap className="h-3 w-3" strokeWidth={1.5} />
            {t("mikraot.build_lesson")}
          </Button>
        </div>
      </div>

      <SaveToNotebookModal
        open={showSaveNotebook}
        onClose={() => setShowSaveNotebook(false)}
        bulkPayloads={notebookPayloads}
        bulkLabel={t("mikraot.bulk_save_label", { count: notebookPayloads.length })}
      />

      <LessonBuilder
        open={showLB}
        onClose={() => setShowLB(false)}
        mode="basket"
        basketItems={basket.items}
      />
      {RequireAuthModal}
    </div>
  );
}

/**
 * Commentary panel skeleton — shown while Sefaria commentaries fetch.
 * Visually mirrors the eventual <CommentaryAccordion> (3-4 collapsed rows
 * with a small dot + text bar) so the perceived layout-shift is minimal
 * and the wait feels short. The .shimmer-bg class drives a slow gold
 * gradient sweep via index.css @keyframes shimmer (2 s linear infinite).
 */
function CommentaryPaneSkeleton() {
  const { t } = useTranslation();
  return (
    <div
      className="space-y-3 py-2"
      data-testid="commentary-pane-skeleton"
      aria-label={t("mikraot.loading_commentators")}
    >
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 px-3 py-3 rounded-sm border border-gold/15 bg-card/40"
        >
          <div className="h-3 w-3 rounded-full shimmer-bg opacity-50" />
          <div className="h-3 flex-1 rounded-sm shimmer-bg opacity-50" style={{ maxWidth: `${65 + i * 5}%` }} />
        </div>
      ))}
    </div>
  );
}
